import posthog from "posthog-js";

import { getPostHogEnvironment, isPostHogEnabled } from "../env";
import { isClient } from "../utils";
import { extractCauseChain, extractOwnProperties } from "./error-context";

let isInitialized = false;

/**
 * Initialize PostHog for client-side analytics.
 * PostHog is only enabled when VERCEL_ENV=production.
 *
 * @param apiKey - The PostHog API key (NEXT_PUBLIC_POSTHOG_KEY)
 * @param options - Additional PostHog configuration options
 */
export function initPostHogClient(
  apiKey: string,
  options?: {
    apiHost?: string;
    uiHost?: string;
    debug?: boolean;
  },
): void {
  if (!isPostHogEnabled()) {
    console.log("[PostHog] Disabled - not in production environment");
    return;
  }

  if (isInitialized) {
    return;
  }

  if (
    process.env.NODE_ENV === "development" &&
    process.env.NEXT_PUBLIC_POSTHOG_ENABLE_DEV === "true"
  ) {
    console.warn(
      "[PostHog] NEXT_PUBLIC_POSTHOG_ENABLE_DEV=true — local events will ship to the PostHog project configured by NEXT_PUBLIC_POSTHOG_KEY. Unset unless you are intentionally testing analytics.",
    );
  }

  posthog.init(apiKey, {
    api_host: options?.apiHost ?? "/ingest",
    ui_host: options?.uiHost ?? "https://us.posthog.com",
    // Latest PostHog defaults (2026-01-30): autocapture, pageview, pageleave,
    // session recording, dead click detection, and exception autocapture
    defaults: "2026-01-30",
    // Automatic exception capture for PostHog Error Tracking
    capture_exceptions: true,
    debug: options?.debug ?? false,
    external_scripts_inject_target: "head",
    // Only create person profiles for identified users (no anonymous profiles)
    person_profiles: "identified_only",
    // Persist a super property on every event so dashboards can filter by env
    loaded: (ph) => {
      ph.register({ environment: getPostHogEnvironment() });
    },
  });

  isInitialized = true;
}

/**
 * Check if PostHog client is enabled and initialized.
 */
export function isPostHogClientEnabled(): boolean {
  return isPostHogEnabled() && isInitialized && isClient();
}

/**
 * Track an event using PostHog.
 *
 * @param eventName - The name of the event to track
 * @param properties - The properties of the event to track
 */
export function trackPostHogClientEvent(
  eventName: string,
  properties: Record<string, unknown> = {},
): void {
  if (!isPostHogClientEnabled()) return;

  posthog.capture(eventName, properties);
}

/**
 * Identify a user in PostHog with their information.
 * This should be called when a user logs in or when their information is available.
 *
 * @param userId - The unique user ID
 * @param properties - User properties (email, firstName, lastName, etc.)
 */
export function identifyPostHogUser(
  userId: string,
  properties: {
    email?: string;
    first_name?: string | null;
    last_name?: string | null;
    [key: string]: unknown;
  } = {},
): void {
  if (!isPostHogClientEnabled()) return;

  // If this device is still identified as a different user (e.g. logout
  // reset was missed, or two accounts share a browser), reset first so
  // this login's events don't get attributed to the previous person.
  if (posthog._isIdentified() && posthog.get_distinct_id() !== userId) {
    posthog.reset();
  }

  // PostHog identify with user properties
  posthog.identify(userId, {
    email: properties.email,
    first_name: properties.first_name,
    last_name: properties.last_name,
    ...properties,
  });
}

/**
 * Reset PostHog session and clear user identification.
 * This should be called when a user logs out.
 */
export function resetPostHog(): void {
  if (!isPostHogClientEnabled()) return;

  posthog.reset();
}

/**
 * Capture an exception in PostHog Error Tracking (replaces Sentry.captureException).
 * Attaches optional extra context as event properties.
 *
 * @param error - The error to capture
 * @param context - Optional extra properties (tags, extra data, etc.)
 */
export function captureException(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  if (!isPostHogClientEnabled()) return;

  const err = error instanceof Error ? error : new Error(String(error));

  // Parity with server: surface Error own-properties (Stripe .code/.type,
  // mailer .email_template, etc) and the .cause chain (drizzle/pg details).
  posthog.captureException(err, {
    ...extractOwnProperties(err),
    ...extractCauseChain(err),
    ...context,
  });
}
